import { flow, getRoot, types } from 'mobx-state-tree';

import { FavoriteTournament } from './favorite-tournament';
import { FavoriteTeam } from './favorite-team';
import llattempt from '../../../../common/utils/attempt';
import api from '../../../../common/api';
import { urls } from '../../../../configs';
import { imageLoading } from '../../../../common/utils';
import { branchConfig, refreshTime } from '../config';
import { llNotifier } from '../../../../components';
import orderBy from 'lodash/orderBy';


const FavMatchItem = types.model('FavMatchItem', {
    id: types.number,
    teamId1: types.maybeNull(types.number),
    teamId2: types.maybeNull(types.number),
    tournamentId: types.maybeNull(types.number),
});

const BettingFavorites = types
    .model('Favorites', {
        tournaments: types.array(FavoriteTournament),
        teams: types.array(FavoriteTeam),
        markets: types.array(types.number),
        tournamentsFetchTime: types.maybeNull(types.Date),
        teamsFetchTime: types.maybeNull(types.Date),
        marketsFetchTime: types.maybeNull(types.Date),
        tournamentsLoading: true,
        teamsLoading: true,
        marketsLoading: false,
        tournamentsCurrentId: types.maybeNull(types.number),
        teamsCurrentId: types.maybeNull(types.number),
        // Search
        searchTournaments: types.array(FavoriteTournament),
        tournamentsSearchValue: types.maybeNull(types.string),
        searchTeams: types.array(FavoriteTeam),
        teamsSearchValue: types.maybeNull(types.string),
        // Matches
        tournamentsMatches: types.array(FavMatchItem),
        tournamentsMatchesFetchTime: types.maybeNull(types.Date),
        teamsMatches: types.array(FavMatchItem),
        teamsMatchesFetchTime: types.maybeNull(types.Date),
    })
    .actions((s) => ({
        _setDataArray(data, propName) {
            s[propName] = orderBy(data, ['order', 'sportId', 'categoryId', 'name'], 'asc');
        },
        _setLoading(val, prop) {
            s[`${prop}Loading`] = val;
        },
        setFetchTime(time, propName) {
            s[propName] = time;
        },
        setSearchVal(val, propName) {
            s[`${propName}SearchValue`] = val;
        },
        setCurrentId(val, propName) {
            s[`${propName}CurrentId`] = val;
        },
        successInstruction({ data, type }) {
            s[`${type}Matches`].clear();
            if (data) {
                getRoot(s).betting.matches.setMatchesData({
                    data,
                    isRanged: true,
                    addFilteredMatchesIds: (id, p1, matchFromStore) => {
                        const newMatch = { id: +id };
                        if (type === 'tournaments') {
                            newMatch.tournamentId = +matchFromStore.tournamentId;
                        }
                        if (type === 'teams') {
                            newMatch.teamId1 = matchFromStore.competitor1.id;
                            newMatch.teamId2 = matchFromStore.competitor2.id;
                        }
                        s[`${type}Matches`].push(newMatch);
                    },
                });
            }
            s.setFetchTime(new Date(), `${type}MatchesFetchTime`);
        },
        unsuccessInstruction({ err }) {
            //     TODO: error
        },
        successUpdateInstruction({ data, type }) {
            s[`${type}Matches`].clear();
            if (data) {
                getRoot(s).betting.matches.setMatchesData({
                    data,
                    isRanged: true,
                    addFilteredMatchesIds: (id, p1, matchFromStore) => {
                        const newMatch = { id: +id };
                        if (type === 'tournaments') {
                            newMatch.tournamentId = +matchFromStore.tournamentId;
                        }
                        if (type === 'teams') {
                            newMatch.teamId1 = matchFromStore.competitor1.id;
                            newMatch.teamId2 = matchFromStore.competitor2.id;
                        }
                        s[`${type}Matches`].push(newMatch);
                    },
                });
            }
            s.setFetchTime(new Date(), `${type}MatchesFetchTime`);
        },
        unsuccessUpdateInstruction({ err }) {
            //     TODO: error
        },
        initializeInstruction: flow(function* fetch({ type }) {
            initializeInstruction: {
                if (s[`${type}MatchesFetchTime`] && Date.now() < +new Date(+s[`${type}MatchesFetchTime`] + refreshTime.favoriteMatches)) {
                    const timeLeft = +new Date(+s[`${type}MatchesFetchTime`] + refreshTime.favoriteMatches) - Date.now();
                    s.setUpdateForEvents({ type, timeLeft });
                    break initializeInstruction;
                }

                s._setLoading(true, type);

                const getFunction = {
                    teams: api.betting.getFavoriteTeamMatches.bind(api.betting),
                    tournaments: api.betting.getFavoriteTournamentsMatches.bind(api.betting),
                };
                const res = yield getFunction[type]({
                    to: branchConfig['All'].to(),
                }) || {};

                if (res.success && !res.data?.error) {
                    s.successInstruction({ data: res.data, type });
                    s.setUpdateForEvents({ type });
                } else {
                    s.unsuccessInstruction({ err: res.data?.error, type });
                }

                s._setLoading(false, type);
            }
        }),
        updateInstruction: flow(function* fetch({ type }) {
            updateInstruction: {
                const sportIdFromUrl = getRoot(s).betting.activeItems.sports[0];
                if (sportIdFromUrl !== 'favorites') {
                    clearTimeout(window[`__fav${type}EventsUpdater`]);
                    break updateInstruction;
                }

                if (!getRoot(s).site?.status.isActive) {
                    s.setUpdateForEvents({ type });
                    break updateInstruction;
                }

                const getFunction = {
                    teams: api.betting.getFavoriteTeamMatches.bind(api.betting),
                    tournaments: api.betting.getFavoriteTournamentsMatches.bind(api.betting),
                };
                const res = yield getFunction[type]({
                    to: branchConfig['All'].to(),
                }) || {};

                if (res.success && !res.data?.error) {
                    s.successUpdateInstruction({ data: res.data, type });
                    s.setUpdateForEvents({ type });
                } else {
                    s.unsuccessUpdateInstruction({ err: res.data?.error, type });
                }
            }
        }),
        setUpdateForEvents({ type, timeLeft }) {
            clearTimeout(window[`__fav${type}EventsUpdater`]);
            window[`__fav${type}EventsUpdater`] = setTimeout(() => s.updateInstruction({ type }), timeLeft ?? refreshTime.favoriteMatches);
        },
        getFavoriteTournaments: flow(function* fetch({ forceRequest } = {}) {
            if (!(s.tournamentsFetchTime && Date.now() < +new Date(+s.tournamentsFetchTime + refreshTime.favorites)) || forceRequest) {
                return llattempt(
                    () =>
                        api.betting.getFavoriteTournaments({}).then((res) => {
                            if (res?.success) {
                                getRoot(s).betting.clearError({
                                    type: 'favoriteTournaments',
                                });
                                const data = res.data.map((item) => ({
                                    id: item.i,
                                    name: item.n,
                                    sportId: item.sid,
                                    categoryId: item.cid,
                                    sportName: item.sn,
                                    categoryName: item.cn,
                                    order: item.o,
                                }));
                                s._setDataArray(data, 'tournaments');
                                s.setFetchTime(new Date(), 'tournamentsFetchTime');
                            } else {
                                getRoot(s).betting.setError({
                                    error: res?.data?.error ?? 'FAVORITE_TOURNAMENTS_TECHNICAL_ISSUES_ERROR',
                                    type: 'favoriteTournaments',
                                    isExactError: true,
                                });
                                s.setFetchTime(null, 'tournamentsFetchTime');
                            }
                            s._setLoading(false, 'tournaments');
                            s.setFetchTime(null, 'tournamentsMatchesFetchTime');
                        }),
                    {
                        withNotifier: false,
                        onError: () => {
                            s._setLoading(false, 'tournaments');
                            s.setFetchTime(null, 'tournamentsFetchTime');
                            s.setFetchTime(null, 'tournamentsMatchesFetchTime');
                            llNotifier({
                                message: 'GENERAL_ERROR',
                                type: 'error',
                            });
                        },
                        at: 'fetchDataForFavoriteTournaments',
                        withParams: {},
                    }
                );
            }
        }),
        getFavoriteTeams: flow(function* fetch({ forceRequest } = {}) {
            if (!(s.teamsFetchTime && Date.now() < +new Date(+s.teamsFetchTime + refreshTime.favorites)) || forceRequest) {
                return llattempt(
                    () =>
                        api.betting.getFavoriteTeams({}).then((res) => {
                            if (res?.success) {
                                getRoot(s).betting.clearError({
                                    type: 'favoriteTeams',
                                });
                                const imgsArr = [];
                                const data = res.data.map((item) => {
                                    const logoUrl = urls.prematchCompetitorImg + item.i + '.png';
                                    imgsArr.push(imageLoading(logoUrl));
                                    return {
                                        id: item.i,
                                        name: item.n,
                                        sportId: item.sid,
                                        categoryId: item.cid,
                                    };
                                });
                                Promise.all(imgsArr).then((res) => {
                                    res.map((resItem, i) => {
                                        data[i].teamHasLogo = resItem;
                                    });
                                    s._setDataArray(data, 'teams');
                                    s._setLoading(false, 'teams');
                                    s.setFetchTime(new Date(), 'teamsFetchTime');
                                    s.setFetchTime(null, 'teamsMatchesFetchTime');
                                });
                            } else {
                                getRoot(s).betting.setError({
                                    error: res?.data?.error ?? 'FAVORITE_TEAMS_TECHNICAL_ISSUES_ERROR',
                                    type: 'favoriteTeams',
                                    isExactError: true,
                                });
                                s._setLoading(false, 'teams');
                                s.setFetchTime(null, 'teamsFetchTime');
                                s.setFetchTime(null, 'teamsMatchesFetchTime');
                            }
                        }),
                    {
                        withNotifier: false,
                        onError: () => {
                            s._setLoading(false, 'teams');
                            s.setFetchTime(null, 'teamsFetchTime');
                            s.setFetchTime(null, 'teamsMatchesFetchTime');
                            llNotifier({
                                message: 'GENERAL_ERROR',
                                type: 'error',
                            });
                        },
                        at: 'fetchDataForFavoriteTeams',
                        withParams: {},
                    }
                );
            }
        }),
        getFavoriteMarkets: flow(function* fetch({ forceRequest } = {}) {
            if (!(s.marketsFetchTime && Date.now() < +new Date(+s.marketsFetchTime + refreshTime.favorites)) || forceRequest) {
                s._setLoading(true, 'markets');
                return llattempt(
                    () =>
                        api.betting.getFavoriteMarkets({}).then((res) => {
                            if (res?.success) {
                                getRoot(s).betting.clearError({
                                    type: 'favoriteMarkets',
                                });
                                const data = orderBy(res.data, 'o', 'asc').map((item) => item.i);
                                s._setDataArray(data, 'markets');
                                s.setFetchTime(new Date(), 'marketsFetchTime');
                                s._setLoading(false, 'markets');
                            } else {
                                getRoot(s).betting.setError({
                                    error: res?.data?.error ?? 'FAVORITE_MARKETS_TECHNICAL_ISSUES_ERROR',
                                    type: 'favoriteMarkets',
                                    isExactError: true,
                                });
                                s._setLoading(false, 'markets');
                                s.setFetchTime(null, 'marketsFetchTime');
                            }
                        }),
                    {
                        withNotifier: false,
                        onError: () => {
                            s._setLoading(false, 'markets');
                            s.setFetchTime(null, 'marketsFetchTime');
                            getRoot(s).betting.setError({
                                error: res?.data?.error ?? 'FAVORITE_MARKETS_TECHNICAL_ISSUES_ERROR',
                                type: 'favoriteMarkets',
                                isExactError: true,
                            });
                        },
                        at: 'fetchDataForFavoriteMarkets',
                        withParams: {},
                    }
                );
            }
        }),
        getSearchTeams: flow(function* fetch({ searchVal, sportId }) {
            s._setLoading(true, 'teams');
            s.setSearchVal(searchVal, 'teams');
            const params = { search: searchVal };
            if (sportId) {
                params.sportId = sportId;
            }
            return llattempt(
                () =>
                    api.betting.searchTeams(params).then((res) => {
                        if (res?.success) {
                            const imgsArr = [];
                            const data = res.data.map((item) => {
                                const logoUrl = urls.prematchCompetitorImg + item.i + '.png';
                                imgsArr.push(imageLoading(logoUrl));
                                return {
                                    id: item.i,
                                    name: item.n,
                                    sportId: item.sid,
                                    categoryId: item.cid,
                                };
                            });
                            Promise.all(imgsArr).then((res) => {
                                res.map((resItem, i) => {
                                    data[i].teamHasLogo = resItem;
                                });
                                s._setDataArray(data, 'searchTeams');
                                s._setLoading(false, 'teams');
                            });
                        } else {
                            llNotifier({
                                message: res?.data?.error ? res.data.error : 'GENERAL_ERROR',
                                type: 'error',
                            });
                        }
                    }),
                {
                    withNotifier: false,
                    at: 'fetchDataForFavoriteTeams',
                    withParams: {},
                }
            );
        }),
        getSearchTournaments: flow(function* fetch({ searchVal, sportId = null }) {
            s._setLoading(true, 'tournaments');
            s.setSearchVal(searchVal, 'tournaments');
            const params = { search: searchVal };
            if (sportId) {
                params.sportId = sportId;
            }
            return llattempt(
                () =>
                    api.betting.searchTournaments(params).then((res) => {
                        if (res?.success) {
                            const data = res.data.map((item) => ({
                                id: item.i,
                                name: item.n,
                                sportId: item.sid,
                                categoryId: item.cid,
                                sportName: item.sn,
                                categoryName: item.cn,
                                order: item.o,
                            }));
                            s._setDataArray(data, 'searchTournaments');
                            s._setLoading(false, 'tournaments');
                        } else {
                            llNotifier({
                                message: res?.data?.error ? res.data.error : 'GENERAL_ERROR',
                                type: 'error',
                            });
                        }
                    }),
                {
                    withNotifier: false,
                    onError: () => {
                        s._setLoading(false, 'tournaments');
                        llNotifier({
                            message: 'GENERAL_ERROR',
                            type: 'error',
                        });
                    },
                    at: 'getSearchTournaments',
                    withParams: {},
                }
            );
        }),
        clearSearchTeams() {
            s.setSearchVal(null, 'teams');
            s._setDataArray([], 'searchTeams');
        },
        clearSearchTournaments() {
            s.setSearchVal(null, 'tournaments');
            s._setDataArray([], 'searchTournaments');
        },
        setFavoriteTournament: flow(function* fetch({ id, sportId, categoryId, isFavorite }) {
            isFavorite = isFavorite ?? s.isTournamentFavorite({ id });
            return llattempt(
                () =>
                    api.betting
                        .setFavoriteTournament({
                            tournamentId: id,
                            sportId,
                            categoryId,
                            del: isFavorite,
                        })
                        .then((res) => {
                            if (res.success) {
                                getRoot(s).betting.favorites.getFavoriteTournaments({
                                    forceRequest: true,
                                });
                                llNotifier({
                                    message: isFavorite ? 'FAV_REMOVED' : 'FAV_ADDED',
                                    type: isFavorite ? 'warn' : 'success',
                                });
                            } else {
                                llNotifier({
                                    message: res?.data?.error ? res.data.error : 'GENERAL_ERROR',
                                    type: 'error',
                                });
                            }
                        }),
                {
                    withNotifier: false,
                    onError: () => {
                        s._setLoading(false, 'tournaments');
                        llNotifier({
                            message: 'GENERAL_ERROR',
                            type: 'error',
                        });
                    },
                    at: 'setFavoriteTournament',
                    withParams: {
                        tournamentId: id,
                        sportId,
                        categoryId,
                        del: isFavorite,
                    },
                }
            );
        }),
        setFavoriteTeam: flow(function* fetch({ competitorId, sportId, isFavorite }) {
            isFavorite = isFavorite ?? s.isTeamFavorite({ competitorId });
            return llattempt(
                () =>
                    api.betting
                        .setFavoriteTeam({
                            competitorId,
                            sportId,
                            del: isFavorite,
                        })
                        .then((res) => {
                            if (res.success) {
                                getRoot(s).betting.favorites.getFavoriteTeams({
                                    forceRequest: true,
                                });
                                llNotifier({
                                    message: isFavorite ? 'FAV_REMOVED' : 'FAV_ADDED',
                                    type: isFavorite ? 'warn' : 'success',
                                });
                            } else {
                                llNotifier({
                                    message: res?.data?.error ? res.data.error : 'GENERAL_ERROR',
                                    type: 'error',
                                });
                            }
                        }),
                {
                    withNotifier: false,
                    onError: () => {
                        s._setLoading(false, 'teams');
                        llNotifier({
                            message: 'GENERAL_ERROR',
                            type: 'error',
                        });
                    },
                    at: 'setFavoriteTeam',
                    withParams: {
                        competitorId,
                        sportId,
                        del: isFavorite,
                    },
                }
            );
        }),
        setFavoriteMarket: flow(function* fetch({ id, isFavorite }) {
            isFavorite = isFavorite ?? s.isMarketFavorite({ id });
            s._setLoading(true, 'markets');
            return llattempt(
                () =>
                    api.betting
                        .setFavoriteMarket({
                            marketId: id,
                            del: isFavorite,
                        })
                        .then((res) => {
                            if (res.success) {
                                getRoot(s).betting.favorites.getFavoriteMarkets({
                                    forceRequest: true,
                                });
                                llNotifier({
                                    message: isFavorite ? 'FAV_REMOVED' : 'FAV_ADDED',
                                    type: isFavorite ? 'warn' : 'success',
                                });
                            } else {
                                llNotifier({
                                    message: res?.data?.error ? res.data.error : 'GENERAL_ERROR',
                                    type: 'error',
                                });
                                s._setLoading(false, 'markets');
                            }
                        }),
                {
                    withNotifier: false,
                    onError: () => {
                        s._setLoading(false, 'markets');
                        llNotifier({
                            message: 'GENERAL_ERROR',
                            type: 'error',
                        });
                    },
                    at: 'setFavoriteMarket',
                    withParams: {
                        marketId: id,
                        del: isFavorite,
                    },
                }
            );
        }),
        clearFavorites() {
            s.tournaments.clear();
            s.tournamentsFetchTime = null;
            s.teams.clear();
            s.teamsFetchTime = null;
            s.tournamentsLoading = true;
            s.teamsLoading = true;
            s.searchTournaments.clear();
            s.tournamentsSearchValue = null;
            s.searchTeams.clear();
            s.teamsSearchValue = null;
            s.tournamentsMatches.clear();
            s.tournamentsCurrentId = null;
            s.tournamentsMatchesFetchTime = null;
            s.teamsMatches.clear();
            s.teamsCurrentId = null;
            s.teamsMatchesFetchTime = null;
            s.markets.clear();
            s.marketsFetchTime = null;
            s.marketsLoading = false;
        },
    }))
    .views((s) => ({
        isTeamFavorite({ competitorId }) {
            return !!s.teams.find((team) => team.id === competitorId);
        },
        isTournamentFavorite({ id }) {
            return !!s.tournaments.find((tournament) => tournament.id === id);
        },
        isMarketFavorite({ id }) {
            return !!s.markets.includes(id);
        },
        favoriteMatches({ type }) {
            let matches = s[`${type}Matches`];
            if (s[`${type}CurrentId`]) {
                matches = matches.filter((item) => {
                    if (type === 'tournaments') {
                        return item.tournamentId === s[`${type}CurrentId`];
                    }
                    if (type === 'teams') {
                        return item.teamId1 === s[`${type}CurrentId`] || item.teamId2 === s[`${type}CurrentId`];
                    }
                });
            }
            matches = matches.map((item) => item.id);
            return s.getGroupedByDateEvents(matches || [], true);
        },
    }));

export default BettingFavorites;